import React from 'react';
import useTheme from '@hero/branding/theme';
import Container from '@hero/ui-kit/layout/Container';
import P from '@hero/ui-kit/typography/P';
import getMemberUrl from '../utils/getMemberUrl';
import * as Style from './style.module.scss';
import HighlightedText from '../../../components/HighlightedText';
import { useLocation, useNavigate } from 'react-router-dom';
import { getBillingFormattedDate } from '../../../utils/date';
import { GetOrganizationMembersParams } from '../api/useGetOrganizationMembers';
import { writeMemberListSnapshot } from '../OrganizationMemberDetails/MemberDetailsWrapper/useMemberListNeighbors';
import {
    ExchangeDeviceInfo,
    MemberRole,
    OrganizationMemberListDeviceInfo,
    StatusInfo
} from '../types';
import DischargeMemberStatusPill from '../components/DischargeMemberStatusPill';
import MemberMismatchAlert from '../components/MemberMismatchAlert';
import CareManagerBadge from '../components/CareManagerBadge';
import DeviceSerial from '../OrganizationMemberList/DeviceSerial';
import getMemberRoleLabel from '../OrganizationMemberList/utils/getMemberRoleLabel';
import { DischargedColumnId } from './columns';

const ListItem: React.FC<{
    memberId?: number | null;
    heroUserId?: number | null;
    name?: string;
    firstName?: string | null;
    lastName?: string | null;
    dateOfBirth?: string | null;
    deviceFirstName?: string | null;
    deviceLastName?: string | null;
    deviceDateOfBirth?: string | null;
    email?: string;
    search: string;
    canOpenDetails?: boolean;
    statusInfo?: StatusInfo | null;
    readmittedTo: number | null;
    careManagerIds: number[] | null;
    externalUserId?: string | null;
    activatedAt?: string | null;
    deviceInfo?: OrganizationMemberListDeviceInfo;
    exchangeDeviceInfo?: ExchangeDeviceInfo;
    role?: MemberRole | null;
    listQuery: GetOrganizationMembersParams;
    isColumnVisible: (id: DischargedColumnId) => boolean;
    gridColumns: string;
}> = ({
    name,
    firstName,
    lastName,
    dateOfBirth,
    deviceFirstName,
    deviceLastName,
    deviceDateOfBirth,
    heroUserId,
    memberId,
    email,
    search,
    statusInfo,
    canOpenDetails = false,
    readmittedTo,
    careManagerIds,
    externalUserId,
    activatedAt,
    deviceInfo,
    exchangeDeviceInfo,
    role,
    listQuery,
    isColumnVisible,
    gridColumns
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();

    const dischargedAt = statusInfo?.discharged_at;
    const cancelledAt = statusInfo?.cancelled_at;
    const reason = statusInfo?.discharge_reason || '';

    const hasId: boolean = React.useMemo(() => {
        return !!memberId || !!heroUserId;
    }, [memberId, heroUserId]);

    const handleNavigate = React.useCallback(
        (e: React.SyntheticEvent) => {
            if (!e.isDefaultPrevented() && hasId && canOpenDetails) {
                const from = `${location.pathname}${location.search}`;
                const state = { from, listQuery, listKind: 'members' as const };
                writeMemberListSnapshot(state);

                if (readmittedTo) {
                    navigate(`${getMemberUrl(null, readmittedTo)}`, { state });
                } else {
                    memberId
                        ? navigate(`${getMemberUrl(null, memberId)}`, { state })
                        : navigate(`${getMemberUrl(heroUserId, memberId)}`, { state });
                }
            }
        },
        [hasId, memberId, heroUserId, navigate, canOpenDetails, location, readmittedTo, listQuery]
    );

    return (
        <div
            onClick={handleNavigate}
            onKeyDown={() => null}
            tabIndex={0}
            role="button"
            style={{ cursor: hasId ? 'pointer' : 'not-allowed' }}
            className={Style.memberListWrapper}
        >
            <Container
                gridTemplateColumns={gridColumns}
                gridColumnGap="small"
                className={Style.container}
            >
                <>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'start',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}
                    >
                        <P noDefaultMargin strong>
                            <HighlightedText highlight={search} text={name} />
                        </P>
                        {email ? (
                            <P
                                size="small"
                                noDefaultMargin
                                styles={{
                                    color: theme.colors.neutrals.textGamma
                                }}
                            >
                                <HighlightedText highlight={search} text={email} />
                            </P>
                        ) : (
                            ''
                        )}
                    </div>
                    {isColumnVisible('patientId') && (
                        <div className="flex-center-vertical">
                            <P noDefaultMargin>
                                <HighlightedText
                                    highlight={search}
                                    text={externalUserId || 'N/A'}
                                />
                            </P>
                        </div>
                    )}
                    {isColumnVisible('memberSince') && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <P noDefaultMargin styles={{ color: theme.colors.neutrals.textGamma }}>
                                {activatedAt
                                    ? getBillingFormattedDate(activatedAt, 'MMM d, yyyy')
                                    : 'Not Activated Yet'}
                            </P>
                        </div>
                    )}
                    {isColumnVisible('device') && (
                        <div className="flex-center-vertical">
                            <DeviceSerial
                                deviceInfo={deviceInfo}
                                search={search}
                                exchangeDeviceInfo={exchangeDeviceInfo}
                            />
                        </div>
                    )}
                    {isColumnVisible('dischargeDate') && (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <P
                                noDefaultMargin
                                styles={{
                                    color: theme.colors.neutrals.textGamma
                                }}
                            >
                                {dischargedAt
                                    ? getBillingFormattedDate(dischargedAt, 'MMM d, yyyy')
                                    : 'No Date'}
                            </P>
                        </div>
                    )}
                    {isColumnVisible('careManager') && (
                        <div className="flex-center-vertical">
                            <CareManagerBadge careManagerIds={careManagerIds} />
                        </div>
                    )}
                    {isColumnVisible('reason') && (
                        <P noDefaultMargin styles={{ display: 'flex', alignItems: 'center' }}>
                            {reason}
                        </P>
                    )}
                    {isColumnVisible('status') && (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <DischargeMemberStatusPill statusInfo={statusInfo} />
                        </div>
                    )}
                    {isColumnVisible('finalizedDate') && (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <P
                                noDefaultMargin
                                styles={{
                                    color: theme.colors.neutrals.textGamma
                                }}
                            >
                                {cancelledAt
                                    ? getBillingFormattedDate(cancelledAt, 'MMM d, yyyy')
                                    : 'No Date'}
                            </P>
                        </div>
                    )}
                    {isColumnVisible('role') && (
                        <div className="flex-center-vertical">
                            <P noDefaultMargin>{getMemberRoleLabel(role)}</P>
                        </div>
                    )}
                    <MemberMismatchAlert
                        firstName={firstName}
                        lastName={lastName}
                        dateOfBirth={dateOfBirth}
                        deviceFirstName={deviceFirstName}
                        deviceLastName={deviceLastName}
                        deviceDateOfBirth={deviceDateOfBirth}
                    />
                </>
            </Container>
        </div>
    );
};

export default ListItem;
